import { useEffect, useRef, useState } from "react";
import { PlatformApiClient, PlatformApiError, type ReviewBoostBillingStatus } from "../api";

export function reviewBoostCheckoutUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const value = new URL(raw);
    return value.protocol==="https:" && value.hostname==="www.mercadopago.com.ar" && !value.username && !value.password
      ? value.href : null;
  } catch {return null;}
}


/** Preserve one key across ambiguous provider retries; rotate only for a new order. */
export function reviewBoostCheckoutKey(
  store: Pick<Storage, "getItem"|"setItem"|"removeItem">,
  organizationId: string,
  retiredOrderId: string | null,
  makeUuid: () => string,
): string {
  const storageKey="rb-checkout:"+organizationId;
  const priorOrderKey="rb-retired-order:"+organizationId;
  // Rotation happens only ONCE per retired order. A network timeout after
  // creating a new subscription must reuse this key on every retry.
  if (retiredOrderId && store.getItem(priorOrderKey)!==retiredOrderId) {
    store.removeItem(storageKey);
    store.setItem(priorOrderKey,retiredOrderId);
  }
  const old=store.getItem(storageKey);
  if (old && /^rb-client-[a-f0-9-]{36}$/.test(old)) return old;
  const key="rb-client-"+makeUuid();
  store.setItem(storageKey,key);
  return key;
}

/** A verified null status means no active order; never reuse a key belonging to retired history. */
export function reviewBoostForgetCheckoutKeyAfterEmptyStatus(
  store: Pick<Storage, "removeItem">,
  organizationId: string,
): void {
  store.removeItem("rb-checkout:"+organizationId);
  store.removeItem("rb-retired-order:"+organizationId);
}

/** Never initiate a first checkout while status lookup is unresolved or failed. */
export function reviewBoostInitialCheckoutReady(
  status: ReviewBoostBillingStatus | null,
  loading: boolean,
  error: string | null,
): boolean {
  return status === null && !loading && error === null;
}

function billingError(cause: unknown): string {
  if (cause instanceof PlatformApiError && cause.status === 503) return "La contratación todavía no está habilitada.";
  if (cause instanceof PlatformApiError && cause.status === 409) return "Hay una suscripción en proceso. Revisá su estado antes de volver a intentar.";
  return "No pudimos comprobar tu suscripción. Intentá nuevamente.";
}

export function ReviewBoostBillingPanel({organizationId,organizationName,api}:{
  organizationId:string;organizationName:string;api:PlatformApiClient;
}) {
  const [status,setStatus] = useState<ReviewBoostBillingStatus|null>(null);
  const [busy,setBusy] = useState(false);
  const [loading,setLoading] = useState(true);
  const [error,setError] = useState<string|null>(null);
  const loaded = useRef<string|null>(null);

  useEffect(()=>{
    if (loaded.current===organizationId) return;
    loaded.current=organizationId;
    let active=true;
    setLoading(true);
    setError(null);
    api.reviewBoostBillingStatus(organizationId).then(result=>{
      if(active){
        // GET null is authoritative; the previous key may belong to a retired order.
        if(result===null) reviewBoostForgetCheckoutKeyAfterEmptyStatus(window.sessionStorage,organizationId);
        setStatus(result);setError(null);setLoading(false);
      }
    }).catch(cause=>{
      if(active){setError(billingError(cause));setLoading(false);}
    });
    return ()=>{active=false;loaded.current=null;};
  },[api,organizationId]);

  async function action(kind:"checkout"|"cancel"|"reconcile") {
    if(busy || (kind==="checkout" && (loading || error !== null))) return;
    if(kind==="cancel" && !window.confirm("¿Confirmás que querés detener los futuros cobros? Seguirás teniendo acceso hasta el final del período pagado.")) return;
    setBusy(true);setError(null);
    try {
      let updated: ReviewBoostBillingStatus|null;
      if (kind==="checkout") {
        // One key per account and browser, reused after an uncertain network result.
        const retiredOrderId=status?.cancellationConfirmed && (
          status.provisioning==="suspended" || status.paidThrough===null
        ) ? status.orderId : null;
        const key=reviewBoostCheckoutKey(window.sessionStorage,organizationId,retiredOrderId,()=>window.crypto.randomUUID());
        updated=await api.reviewBoostBillingCheckout(organizationId,key);
      } else if (kind==="cancel") {
        updated=await api.reviewBoostBillingCancel(organizationId);
      }
      else updated=await api.reviewBoostBillingReconcile(organizationId);
      setStatus(updated);
    } catch(cause){setError(billingError(cause));}
    finally {setBusy(false);}
  }
  const checkout=reviewBoostCheckoutUrl(status?.checkoutUrl);
  const paidThrough=status?.paidThrough?new Date(status.paidThrough).toLocaleDateString("es-AR"):null;
  const cancelled=Boolean(status?.cancelAtPeriodEnd||status?.cancellationConfirmed);
  const active=Boolean(status && status.provisioning==="ready" && !cancelled);

  return <section className="details-card rb-billing" aria-label={"Review Boost para "+organizationName}>
    <h2>Review Boost · {organizationName}</h2>
    <p>ARS 20.000 / mes · Mercado Pago</p>
    {loading ? <p role="status">Consultando suscripción…</p> : null}
    {error && <p role="alert" className="inline-alert">{error}</p>}
    {!loading && !error && !status && <p>Todavía no hay una suscripción contratada para este negocio.</p>}
    {status && <>
      <p role="status">Estado: {status.provisioning==="ready"?"Acceso habilitado":status.provisioning==="pending"?"Activación en curso":status.provisioning==="suspended"?"Acceso suspendido":"Pago pendiente"}</p>
      {paidThrough && <p>Período pagado hasta: {paidThrough}</p>}
      {cancelled && <p>Renovación cancelada. No se realizarán nuevos cobros de esta suscripción.</p>}
      {status.graceUntil && <p>Pago de renovación rechazado: período de gracia hasta {new Date(status.graceUntil).toLocaleDateString("es-AR")}.</p>}
      {status.recoverable && <p>Hay una operación que necesita reconciliación.</p>}
      {checkout && !cancelled && status.provisioning!=="ready" && <a href={checkout} rel="noreferrer">Continuar el pago seguro en Mercado Pago</a>}
    </>}
    <div className="button-row">
      {reviewBoostInitialCheckoutReady(status,loading,error) && <button type="button" disabled={busy} onClick={()=>void action("checkout")}>Contratar</button>}
      {status && !cancelled && <button type="button" disabled={busy} onClick={()=>void action("reconcile")}>Actualizar estado</button>}
      {status && !cancelled && active && <button type="button" disabled={busy} onClick={()=>void action("cancel")}>Cancelar renovación</button>}
      {status && !error && cancelled && (status.provisioning==="suspended" || status.paidThrough===null) &&
        <button type="button" disabled={busy} onClick={()=>void action("checkout")}>Volver a contratar</button>}
    </div>
    <p className="rb-signup-note">El regreso del checkout no significa pago aprobado. El acceso se activa únicamente tras verificar el cobro con Mercado Pago.</p>
  </section>;
}
